// src/components/managed-sources/ShopifyAuthStep.tsx

import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconPlugConnected,
  IconPlus,
  IconShoppingBag,
  IconX,
} from "@tabler/icons-react";

import {
  exchangeShopifyOAuth,
  listAuthorizations,
  startShopifyOAuth,
} from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import { useShopifyOAuthPopup } from "@/hooks/useShopifyOAuthPopup";

type Props = {
  authorizationId: string | null;
  onAuthorizationChange: (id: string | null) => void;
  /** Reports the connected store's display label (shop name / connection name)
   *  so the review step can show "Store: …" instead of an empty field. */
  onStoreLabelChange?: (label: string | null) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
};

const SHOP_DOMAIN_RE = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

/** Forgive pasted browser URLs: strip the scheme, anything after the domain
 * (a trailing slash or an admin path), and surrounding whitespace — the field
 * wants the bare `yourstore.myshopify.com`. */
export const normalizeShopDomain = (raw: string): string =>
  raw.trim().replace(/^https?:\/\//i, "").split("/")[0];

export default function ShopifyAuthStep({
  authorizationId,
  onAuthorizationChange,
  onStoreLabelChange,
  onConnectionNameChange,
  onValidated,
}: Props) {
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);
  const [mode, setMode] = useState<"existing" | "new">("existing");

  const [connectionName, setConnectionName] = useState("");
  const [shopDomain, setShopDomain] = useState("");
  const [localError, setLocalError] = useState("");
  const stateRef = useRef<string | null>(null);

  async function handleComplete({ query }: { query: string }) {
    const result = await exchangeShopifyOAuth(query);
    onAuthorizationChange(result.authorization_id);
    onStoreLabelChange?.(result.shop_name || connectionName.trim() || null);
    await loadAuths();
  }

  function selectExisting(id: string | null) {
    onAuthorizationChange(id);
    onStoreLabelChange?.(
      id ? existingAuths.find((a) => a.id === id)?.name || null : null,
    );
  }

  const {
    openPopup,
    loading: oauthLoading,
    error: hookError,
  } = useShopifyOAuthPopup({
    getExpectedState: () => stateRef.current,
    onComplete: handleComplete,
  });

  async function loadAuths() {
    setLoadingAuths(true);
    try {
      const auths = await listAuthorizations({ type: "SHOPIFY" });
      setExistingAuths(auths);
      if (auths.length === 0) setMode("new");
    } catch {
      // Non-fatal — the user can still connect a new store.
    } finally {
      setLoadingAuths(false);
    }
  }

  useEffect(() => {
    loadAuths();
  }, []);

  useEffect(() => {
    onValidated(!!authorizationId);
  }, [authorizationId, onValidated]);

  const shopValid = SHOP_DOMAIN_RE.test(shopDomain.trim().toLowerCase());
  const canConnect = connectionName.trim().length > 0 && shopValid && !oauthLoading;

  async function handleConnect() {
    if (!canConnect) return;
    setLocalError("");
    try {
      const { authorize_url, state } = await startShopifyOAuth(
        connectionName.trim(),
        shopDomain.trim().toLowerCase(),
      );
      stateRef.current = state;
      openPopup(authorize_url);
    } catch (e: any) {
      stateRef.current = null;
      setLocalError(e?.message || "We couldn't start the Shopify connection. Please try again.");
    }
  }

  const error = hookError || localError || "";
  const selectOptions = existingAuths.map((a) => ({ value: a.id, label: a.name }));

  return (
    <Stack gap="lg">
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="green">
          <IconShoppingBag size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Shopify
          </Text>
          <Text size="sm" c="dimmed">
            Connect your Shopify store so Verity can import your orders, customers and products.
          </Text>
        </Stack>
      </Group>

      {loadingAuths ? (
        <Stack align="center" justify="center" py={40}>
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading Shopify connections...
          </Text>
        </Stack>
      ) : (
        <>
          {existingAuths.length > 0 && mode === "existing" && (
            <Stack gap="md">
              <Select
                label="Shopify connection"
                placeholder="Select a connected store"
                data={selectOptions}
                value={authorizationId}
                onChange={selectExisting}
                size="md"
              />
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Need a different store?
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => {
                    selectExisting(null);
                    setMode("new");
                  }}
                >
                  Connect another store
                </Button>
              </Group>
            </Stack>
          )}

          {mode === "new" && (
            <Stack gap="md">
              <TextInput
                label="Connection name"
                placeholder="My Shopify store"
                value={connectionName}
                onChange={(e) => {
                  setConnectionName(e.currentTarget.value);
                  onConnectionNameChange?.(e.currentTarget.value);
                }}
                size="md"
              />
              <TextInput
                label="Store URL"
                placeholder="yourstore.myshopify.com"
                value={shopDomain}
                onChange={(e) => setShopDomain(normalizeShopDomain(e.currentTarget.value))}
                error={
                  shopDomain.trim() && !shopValid
                    ? "Enter your store as yourstore.myshopify.com"
                    : undefined
                }
                size="md"
              />
              <Button
                onClick={handleConnect}
                disabled={!canConnect}
                loading={oauthLoading}
                variant="filled"
                color="green"
              >
                Connect with Shopify
              </Button>
              <Text size="xs" c="dimmed">
                A Shopify window opens where you approve read-only access. You stay signed in here.
              </Text>
              {existingAuths.length > 0 && (
                <Button variant="subtle" size="xs" onClick={() => setMode("existing")}>
                  Use an existing connection instead
                </Button>
              )}
            </Stack>
          )}
        </>
      )}

      {error && (
        <Alert color="red" icon={<IconX size={16} />}>
          {error}
        </Alert>
      )}

      {authorizationId && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                {existingAuths.find((a) => a.id === authorizationId)?.name ||
                  "Shopify store connected"}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
